import { inject, Injectable } from '@angular/core';
import { toObservable } from '@angular/core/rxjs-interop';
import { CoreStore } from '@core/core.store';
import { APP_NAME } from '@core/models/app.models';
import { GqlClientService } from '@shared/services/gql/gql-server.service';
import { ExtractDataUtils } from '@shared/utils/extract-data.util';
import { firstValueFrom, type Observable } from 'rxjs';
import { filter, map, take } from 'rxjs/operators';
import { P_TOURS_STORAGE_KEY } from './p-tour-config';
import type { IJkitTourPersistenceSavePayload, IJkitTourProgressRecord, TJkitTourUserState } from './tour.model';
import { TourQueries } from './tour.queries';
import type { IJKitTourPersistenceProvider } from './tour.service';
import { readJkitTourProgress, writeJkitTourProgress } from './tour.storage';

const COMPLETED_STATE: TJkitTourUserState = 'Completed';

@Injectable({ providedIn: 'root' })
export class PTourPersistenceService implements IJKitTourPersistenceProvider {
	private readonly _gql = inject(GqlClientService);
	private readonly _coreStore = inject(CoreStore);
	private readonly _userId$ = toObservable(this._coreStore.user).pipe(
		map(user => user?.profile),
		filter((profile): profile is string => !!profile),
		take(1)
	);

	public resolveRecordMeta = (): Partial<IJkitTourProgressRecord> => ({
		UserId: this._coreStore.user()?.profile ?? undefined,
		App: APP_NAME
	});

	public async read(): Promise<readonly IJkitTourProgressRecord[]> {
		const userId = await firstValueFrom(this._userId$);
		const records = await firstValueFrom(this._fetchAll(userId));
		return Array.isArray(records) ? records : [];
	}

	public async save(payload: IJkitTourPersistenceSavePayload): Promise<void> {
		const userId = await firstValueFrom(this._userId$);
		const tourId = payload.record.TourId;

		if (payload.existed) {
			await firstValueFrom(this._update(userId, tourId));
			return;
		}

		await firstValueFrom(this._create(userId, tourId));
	}

	public async deleteTour(tourId: string): Promise<void> {
		const userId = await firstValueFrom(this._userId$);
		await firstValueFrom(this._delete(userId, tourId));
		this._removeLocalRecord(tourId);
	}

	private _fetchAll(userId: string): Observable<IJkitTourProgressRecord[] | undefined> {
		return this._gql.pip
			.query({
				query: TourQueries.GET_ALL,
				errorPolicy: 'ignore',
				fetchPolicy: 'network-only',
				variables: { userId, app: APP_NAME }
			})
			.pipe(ExtractDataUtils.extract<IJkitTourProgressRecord[] | undefined>(['data', 'getAllMyToursPref']));
	}

	private _create(userId: string, tourId: string): Observable<unknown> {
		return this._gql.pip.mutate({
			mutation: TourQueries.CREATE,
			errorPolicy: 'ignore',
			variables: { app: APP_NAME, userId, tourId, state: COMPLETED_STATE }
		});
	}

	private _update(userId: string, tourId: string): Observable<unknown> {
		return this._gql.pip.mutate({
			mutation: TourQueries.UPDATE,
			errorPolicy: 'ignore',
			variables: { app: APP_NAME, userId, tourId, state: COMPLETED_STATE }
		});
	}

	private _delete(userId: string, tourId: string): Observable<unknown> {
		return this._gql.pip.mutate({
			mutation: TourQueries.DELETE,
			errorPolicy: 'ignore',
			variables: { app: APP_NAME, userId, tourId }
		});
	}

	private _removeLocalRecord(tourId: string): void {
		const remaining = readJkitTourProgress(P_TOURS_STORAGE_KEY).filter(record => record.TourId !== tourId);
		writeJkitTourProgress(P_TOURS_STORAGE_KEY, remaining);
	}
}
